import React, {
    useState, useCallback, useContext, useMemo, useRef, useEffect
} from 'react';
import { WizardPage } from 'gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import SgVehicleMileage from 'sg-spm-vehicle-mileage';
import SgVehicleUse from 'sg-spm-vehicle-use';
import SgCepMotorAddons from 'sg-cep-motor-addons';
import SgCepSideMenu from 'sg-cep-side-menu';
import SgWebchatInfo from 'sg-webchat-info';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import {
    SgCepChanges,
    SgQuoteContext,
    useSgTrackPageName,
    SgNumberUtil,
    SgActiveTestsContext,
    SgSessionStorageService
} from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import SpmConstants from 'sg-spm-constants';
import { CepConstants } from 'saga-cep-specific-components';
import _ from 'lodash';
import { RenewalService } from 'gw-capability-renewal';
import { Loader } from '@jutro/components';
import metadata from './SgRenewalChangesPage.metadata.json5';
import metadataWOC4177 from './SgRenewalChangesPage_WOC4177.metadata.json5';
import messages from './SgRenewalChangesPage.messages';

const pageId = 'RenewalChangesPage';

function SgRenewalChangesPage(props) {
    const {
        wizardData: modelVM,
        updateWizardData: updateData,
        sgWizardPropsExt,
        goNext
    } = props;

    const {
        handleCancel
    } = sgWizardPropsExt;

    const { activeMvtTests } = useContext(SgActiveTestsContext);
    const intl = useContext(IntlContext);
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    useSgTrackPageName(CepConstants.PAGE_NAMES.CHANGE_POLICY);

    const getValue = useCallback((value) => {
        const typelistValue = viewModelService.productMetadata.get('pc').types.getTypelist(value.typelist.name).getCode(value.code).name;
        return translator({ id: typelistValue, defaultMessage: typelistValue }).toString();
    }, [translator, viewModelService.productMetadata]);

    const changes = SgCepChanges.getMotorChanges(modelVM, getValue, true);

    const volExcess = useRef(_.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.requestedVolExcess.value.code'));
    const vehicleUseAndMileage = useRef({
        lastVehicleUseValue: _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.vehicleUse.value.code'),
        lastVehicleMileageValue: _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.mileage.value.code')
    });
    const [quoting, setQuoting] = useState(false);
    const [isLoading, setLoadingState] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    const quoteContext = {
        quoting,
        setQuoting
    };
    const {
        initialValidation,
        onValidate,
        isComponentValid
    } = useValidation(pageId);

    const onNext = useCallback(async () => {
        if (!_.isNil(isComponentValid) && !isComponentValid) {
            setShowErrors(true);
            return false; // Do not continue
        }
        return modelVM;
    }, [modelVM, isComponentValid]);

    const commonProps = {
        modelVM,
        updateData,
        showErrors,
        onValidate,
        messageParams: {
            vehicle: SpmConstants.CAR
        }
    };

    const onContinue = useCallback(async () => {
        if (!_.isNil(isComponentValid) && !isComponentValid) {
            setShowErrors(true);
            return; // do not continue
        }
        setLoadingState(true);
        const result = await RenewalService.save(modelVM.value);
        const modelCopy = _.clone(modelVM);
        modelCopy.value = result;
        updateData(modelCopy);
        setLoadingState(false);
        goNext();
    }, [modelVM, setShowErrors, goNext, isComponentValid]);

    const hasExcessChanged = useMemo(() => {
        return volExcess.current !== _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.requestedVolExcess.value.code');
    }, [_.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.requestedVolExcess.value.code')]);

    const updateCoveragesAndCalculate = useCallback(() => {
        if (!_.isNil(isComponentValid) && !isComponentValid) {
            setShowErrors(true);
            return; // do not continue
        }
        setLoadingState(true);
        RenewalService.updateCoveragesAndQuote(modelVM.value)
            .then((response) => {
                const newModelVM = _.clone(modelVM);
                newModelVM.value = response;
                updateData(newModelVM);
                SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, newModelVM.value);
                setLoadingState(false);
            });
    }, [isComponentValid, modelVM, updateData]);

    useEffect(() => {
        const currentVehicleUseValue = _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.vehicleUse.value.code');
        const currentVehicleMileageValue = _.get(modelVM, 'lobData.privateMotor.coverables.spmVehicle.mileage.value.code');
        if (activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_4177)
            && (vehicleUseAndMileage.current.lastVehicleUseValue !== currentVehicleUseValue
                || vehicleUseAndMileage.current.lastVehicleMileageValue !== currentVehicleMileageValue)) {
            vehicleUseAndMileage.current = {
                lastVehicleUseValue: currentVehicleUseValue,
                lastVehicleMileageValue: currentVehicleMileageValue
            };
            updateCoveragesAndCalculate();
        }
    }, [isComponentValid, modelVM, updateData, updateCoveragesAndCalculate, activeMvtTests]);

    const coverCalculatorMobileContent = useMemo(() => {
        return (
            <div className="sg-cover-calculator-header" id="sgMobileHeader">
                <div className="container">
                    <p className="title">{ translator(messages.CoverCalculator) }</p>
                    {Object.keys(changes).length === 0 || !modelVM.value.totalCost
                        ? <p className="sg-make-change">{ translator(messages.MakeChangeToCalculate) }</p>
                        : (
                            <>
                                <div className="sg-new-price-container">
                                    <p className="sg-new-price-wording">{ translator(messages.NewAnnualPrice) }</p>
                                    <p className="sg-new-price-value">
                                        { SgNumberUtil.getCurrencyValue(intl, modelVM.value.totalCost) }
                                    </p>
                                </div>
                            </>
                        )}
                </div>
            </div>
        );
    }, [translator, changes, modelVM]);

    const overrides = {
        vehicleMileageComponent: {
            ...commonProps
        },
        vehicleUseComponent: {
            ...commonProps
        },
        cepMotorAddonsComponent: {
            ...commonProps,
            updateCoveragesAndCalculate: updateCoveragesAndCalculate
        },
        continuePolicyChangeButton: {
            disabled: Object.keys(changes).length === 0 && !hasExcessChanged
        },
        coverCalculatorMobile: {
            content: coverCalculatorMobileContent
        },
        coverValue: {
            content: Object.keys(changes).length === 0 || !modelVM.value.totalCost
                ? translator(messages.MakeChangeToCalculate)
                : SgNumberUtil.getCurrencyValue(intl, modelVM.value.totalCost),
            className: modelVM.value.additionalPaymentInformation ? 'sg-new-price-value' : 'sg-new-price-prompt'
        },
        cepRenewalPremiumWebchatMessage: {
            showMtaMessage: true
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate,
            handleCancel,
            onContinue
        },
        resolveComponentMap: {
            SgVehicleMileage,
            SgVehicleUse,
            SgCepMotorAddons,
            SgWebchatInfo,
            SgCepSideMenu
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <SgQuoteContext.Provider value={quoteContext}>
            <WizardPage
                onNext={onNext}
                pid={pageId}
                skipWhen={initialValidation}
                forceParentOnNextExt
                showPrevious={false}
            >
                <ViewModelForm
                    uiProps={activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_4177)
                        ? metadataWOC4177.pageContent : metadata.pageContent}
                    model={modelVM}
                    overrideProps={overrides}
                    onModelChange={updateData}
                    onValidationChange={onValidate}
                    showErrors={showErrors}
                    callbackMap={resolvers.resolveCallbackMap}
                    componentMap={resolvers.resolveComponentMap}
                />
            </WizardPage>
        </SgQuoteContext.Provider>
    );
}

export default SgRenewalChangesPage;
